import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { errorMessage, formatMoney, isPassPlan, tierName } from "./first-round";

type Plan = {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  tagline: string | null;
  tier: string;
  monthlyPrice: string | number;
  compareAtPrice: string | number | null;
  offerLabel: string | null;
  currency: string;
  accessDays: number | null;
  isPublic: boolean;
  sortOrder: number | null;
};

const QUERY_KEY = ["plans", "THERESUME"];

/**
 * theResume's plans as First Round sells them: Free, and Starter / Active passes. What's saved
 * here shows on firstround.com.au and in theResume's plan dialog within about a minute.
 */
export function FirstRoundPlans() {
  const {
    data: plans,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminApi.listPlans("THERESUME") as Promise<Plan[]>,
  });

  if (isLoading) return <div>Loading...</div>;
  if (error || !plans)
    return <div className="text-red-600">Couldn't load plans: {errorMessage(error)}</div>;

  const sorted = [...plans].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));
  const sold = sorted.filter((p) => p.tier === "FREE" || isPassPlan(p));
  const retired = sorted.filter((p) => !sold.includes(p));

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Plans · First Round</h1>
        <p className="mt-1 text-sm text-gray-600">
          The plans on firstround.com.au and in theResume's plan dialog. A new price applies to the
          next checkout; passes already bought keep what they paid and their length. The numbers on
          the cards (resumes, practice interviews, demo tries) are set on{" "}
          <Link className="underline" to="/admin/features">
            Features
          </Link>
          .
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {sold.map((plan) => (
          <PlanEditor key={plan.id} plan={plan} />
        ))}
      </div>

      {retired.length > 0 && (
        <div className="text-sm text-gray-500">
          <p className="font-medium text-gray-700">Not sold</p>
          <ul className="mt-1 list-disc pl-5">
            {retired.map((p) => (
              <li key={p.id}>
                {p.displayName} ({p.name}) · old monthly plan, hidden from pricing
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PlanEditor({ plan }: { plan: Plan }) {
  const qc = useQueryClient();
  const isFree = plan.tier === "FREE";
  const isPass = isPassPlan(plan);
  const initial = {
    displayName: plan.displayName,
    description: plan.description ?? "",
    tagline: plan.tagline ?? "",
    monthlyPrice: String(Number(plan.monthlyPrice)),
    compareAtPrice: plan.compareAtPrice === null ? "" : String(Number(plan.compareAtPrice)),
    offerLabel: plan.offerLabel ?? "",
    accessDays: plan.accessDays ? String(plan.accessDays) : "",
    sortOrder: plan.sortOrder === null ? "" : String(plan.sortOrder),
    isPublic: plan.isPublic,
  };
  const [form, setForm] = useState(initial);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setMessage(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const dto: Record<string, unknown> = {
        displayName: form.displayName,
        description: form.description,
        tagline: form.tagline,
        isPublic: form.isPublic,
      };
      if (!isFree) {
        dto.monthlyPrice = Number(form.monthlyPrice);
        dto.compareAtPrice = form.compareAtPrice === "" ? null : Number(form.compareAtPrice);
        dto.offerLabel = form.offerLabel;
      }
      if (isPass) dto.accessDays = Number(form.accessDays);
      if (form.sortOrder !== "") dto.sortOrder = Number(form.sortOrder);
      return adminApi.updatePlan(plan.id, dto);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      setMessage({ ok: true, text: "Saved. The cards update within about a minute." });
    },
    onError: (e) => setMessage({ ok: false, text: errorMessage(e) }),
  });

  const priceChanged = !isFree && form.monthlyPrice !== initial.monthlyPrice;
  const onOffer =
    !isFree && form.compareAtPrice !== "" && Number(form.compareAtPrice) > Number(form.monthlyPrice);

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{plan.displayName}</CardTitle>
          <Badge variant="outline">{tierName("THERESUME", plan.tier)}</Badge>
        </div>
        <CardDescription>
          {isFree
            ? "Free · no card"
            : `${formatMoney(plan.monthlyPrice, plan.currency)} ${plan.currency} one-off · ${
                plan.accessDays
              } days${
                plan.compareAtPrice !== null
                  ? ` · offer, usually ${formatMoney(plan.compareAtPrice, plan.currency)}`
                  : ""
              }`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="Name on the card">
          <Input
            value={form.displayName}
            maxLength={40}
            onChange={(e) => set("displayName", e.target.value)}
          />
        </Field>
        <Field label="Subtitle">
          <Input
            value={form.description}
            maxLength={160}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>
        <Field label="Badge (optional)" hint="e.g. Best value. Leave empty for none.">
          <Input
            value={form.tagline}
            maxLength={24}
            onChange={(e) => set("tagline", e.target.value)}
          />
        </Field>
        {!isFree && (
          <Field label={`Price (${plan.currency}, one-off)`}>
            <Input
              type="number"
              min={0}
              step="1"
              value={form.monthlyPrice}
              onChange={(e) => set("monthlyPrice", e.target.value)}
            />
          </Field>
        )}
        {!isFree && (
          <Field
            label={`Usual price, struck through (${plan.currency})`}
            hint="Shown crossed out next to the price. Leave empty for no offer."
          >
            <Input
              type="number"
              min={0}
              step="1"
              value={form.compareAtPrice}
              onChange={(e) => set("compareAtPrice", e.target.value)}
            />
          </Field>
        )}
        {!isFree && form.compareAtPrice !== "" && (
          <Field label="Offer label" hint="Shown above the price, e.g. Introductory offer.">
            <Input
              value={form.offerLabel}
              maxLength={30}
              onChange={(e) => set("offerLabel", e.target.value)}
            />
          </Field>
        )}
        {onOffer && (
          <p className="text-xs text-gray-600">
            Cards show:{" "}
            <span className="font-medium uppercase tracking-wide text-blue-700">
              {form.offerLabel || "(no label)"}
            </span>{" "}
            <s>{formatMoney(form.compareAtPrice, plan.currency)}</s>{" "}
            <strong>{formatMoney(form.monthlyPrice || 0, plan.currency)}</strong>
          </p>
        )}
        {isPass && (
          <Field label="Days of access" hint="Counted from the first practice interview.">
            <Input
              type="number"
              min={1}
              max={366}
              value={form.accessDays}
              onChange={(e) => set("accessDays", e.target.value)}
            />
          </Field>
        )}
        <Field label="Order on the page">
          <Input
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(e) => set("sortOrder", e.target.value)}
          />
        </Field>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Shown on the pricing cards</span>
          <Switch
            checked={form.isPublic}
            onCheckedChange={(v) => set("isPublic", v)}
            aria-label="Shown on the pricing cards"
          />
        </label>

        {priceChanged && (
          <p className="rounded-sm bg-amber-50 p-2 text-xs text-amber-900">
            New checkouts will charge {formatMoney(form.monthlyPrice || 0, plan.currency)}.
          </p>
        )}
        {message && (
          <p
            role="status"
            className={`flex items-center gap-1 text-xs ${
              message.ok ? "text-green-700" : "text-red-600"
            }`}
          >
            {message.ok && <CheckCircle2 className="h-3.5 w-3.5" />}
            {message.text}
          </p>
        )}
        <div className="flex gap-2">
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving..." : "Save"}
          </Button>
          {dirty && (
            <Button size="sm" variant="outline" onClick={() => setForm(initial)}>
              Undo
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1 text-sm font-medium text-slate-700">
      <span>{label}</span>
      {children}
      {hint && <span className="block text-xs font-normal text-slate-500">{hint}</span>}
    </label>
  );
}
